/* Copyright (c) 2023-2025 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useEffect, useReducer } from "react";
import { When } from "react-if";
import styled from "styled-components";
import { Button, SVGIcon, theme } from "@nordcloud/gnui";
import { Resource } from "~/generated/graphql";
import { PopoverControls } from "~/components";
import { AddResourceGroupSidebar } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/AddResourceGroupSidebar";
import { AddResourcesSidebar } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/AddResourcesSidebar";
import {
  AddResourceSidebarState,
  SidebarType,
} from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/components/AddResources/types";
import { useStepState } from "~/views/plans/PlanCreate/components/PlanCreateWizard/PlanSettingsForms/StepProvider";

type Props = {
  refetchResourceGroups: () => void;
  refetchResources: () => void;
  alreadyAddedResourceIds?: Set<Resource["id"]>;
};

export function AddResources({
  refetchResourceGroups,
  refetchResources,
  alreadyAddedResourceIds,
}: Props) {
  const { stepState } = useStepState();

  const [state, updateState] = useReducer(
    (
      data: AddResourceSidebarState,
      partialData: Partial<AddResourceSidebarState>
    ) => ({
      ...data,
      ...partialData,
    }),
    {
      selectedResourceGroupIds:
        stepState.selectedEntity?.resourceGroupIds ?? [],
      selectedResourceIds: stepState.selectedEntity?.resourceIds ?? [],
      activeSidebar: undefined,
    }
  );

  const openSideBar = (sidebarType: SidebarType) => {
    updateState({
      activeSidebar: sidebarType,
    });
  };

  useEffect(() => {
    updateState({
      selectedResourceGroupIds: stepState.selectedEntity?.resourceGroupIds,
      selectedResourceIds: stepState.selectedEntity?.resourceIds,
    });
  }, [stepState.selectedEntity]);

  const isResourceGroupSidebarOpen =
    state.activeSidebar === SidebarType.RESOURCE_GROUP;
  const isResourceSidebarOpen = state.activeSidebar === SidebarType.RESOURCE;

  return (
    <>
      <div css={{ display: "flex", justifyContent: "flex-end" }}>
        <PopoverControls
          placement="bottom"
          trigger={
            <Button severity="low">
              <TriggerWrap>
                Add Resources
                <SVGIcon name="down" />
              </TriggerWrap>
            </Button>
          }
          disabled={false}
          items={[
            {
              label: "Add Resource Groups",
              onClick: () => openSideBar(SidebarType.RESOURCE_GROUP),
            },
            {
              label: "Add Resources",
              onClick: () => openSideBar(SidebarType.RESOURCE),
            },
          ]}
        />
      </div>

      <When condition={isResourceGroupSidebarOpen}>
        <AddResourceGroupSidebar
          refetch={refetchResourceGroups}
          sidebarState={state}
          updateSidebarState={updateState}
        />
      </When>
      <When condition={isResourceSidebarOpen}>
        <AddResourcesSidebar
          refetch={refetchResources}
          sidebarState={state}
          updateSidebarState={updateState}
          alreadyAddedResourceIds={alreadyAddedResourceIds}
        />
      </When>
    </>
  );
}
const TriggerWrap = styled("div")<{ isDark?: boolean }>`
  display: flex;
  gap: ${theme.spacing.spacing02};
  cursor: pointer;
`;
